// DumbbellPlot.js
import { useMemo } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { colors } from "../utils";

const Plot = createPlotlyComponent(Plotly);

const DumbbellPlot = ({
  isLoading,
  data, // keyed by proxy uuid
  timebucketIdxs, // [fromIdx, toIdx]
  activeDomains, // [{label, value}]
  activeProxies, // [{label, value}]
}) => {
  const hasTwoDomains = (activeDomains?.length ?? 0) === 2;
  const hasTwoBuckets = (timebucketIdxs?.length ?? 0) === 2;
  const hasProxies = (activeProxies?.length ?? 0) > 0;

  const idxFrom = hasTwoBuckets ? timebucketIdxs[0] : null;
  const idxTo = hasTwoBuckets ? timebucketIdxs[1] : null;

  const isReady =
    !isLoading &&
    !!data &&
    hasProxies &&
    hasTwoDomains &&
    typeof idxFrom === "number" &&
    typeof idxTo === "number" &&
    idxFrom >= 0 &&
    idxTo >= 0;

  // --------------------
  // Layout (autosize, square, centered)
  // --------------------
  const chartLayout = useMemo(() => {
    if (!isReady) return null;

    return {
      autosize: true,
      title: "",

      xaxis: {
        title: {
          text: activeDomains[0]?.label ?? "",
          font: { size: 14, color: "white" },
          standoff: 20,
        },
        range: [0, 100],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
        zeroline: false,
      },

      yaxis: {
        title: {
          text: activeDomains[1]?.label ?? "",
          font: { size: 14, color: "white" },
          standoff: 20,
        },
        range: [0, 100],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
        zeroline: false,

        // keep same scale as X
        scaleanchor: "x",
        scaleratio: 1,
      },

      // Slightly tighter symmetric margins helps centering
      margin: { l: 60, r: 60, t: 20, b: 110 },

      legend: {
        orientation: "h",
        x: 0.5,
        xanchor: "center",
        y: -0.25,
        yanchor: "top",
        font: { color: "white" },
      },

      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(0,0,0,0)",
      font: { color: "white" },
      showlegend: true,
    };
  }, [isReady, activeDomains]);

  // --------------------
  // Data
  // --------------------
  const chartData = useMemo(() => {
    if (!isReady) return null;

    const d0 = activeDomains[0]?.value;
    const d1 = activeDomains[1]?.value;
    if (!d0 || !d1) return null;

    const getAt = (arr, i) => (Array.isArray(arr) ? arr[i] : undefined);

    const traces = (activeProxies ?? [])
      .map((proxy, idx) => {
        const proxyId = proxy?.value;
        const proxyName = proxy?.label;
        if (!proxyId || !proxyName) return null;

        const profile = data?.[proxyId];
        if (!profile) return null;

        const sx = profile?.[d0];
        const sy = profile?.[d1];
        if (!sx || !sy) return null;

        const scoreX = [getAt(sx.value, idxFrom), getAt(sx.value, idxTo)];
        const lbX = [getAt(sx.value_lb, idxFrom), getAt(sx.value_lb, idxTo)];
        const ubX = [getAt(sx.value_ub, idxFrom), getAt(sx.value_ub, idxTo)];

        const scoreY = [getAt(sy.value, idxFrom), getAt(sy.value, idxTo)];
        const lbY = [getAt(sy.value_lb, idxFrom), getAt(sy.value_lb, idxTo)];
        const ubY = [getAt(sy.value_ub, idxFrom), getAt(sy.value_ub, idxTo)];

        const allNums = [...scoreX, ...lbX, ...ubX, ...scoreY, ...lbY, ...ubY].every(
          (v) => typeof v === "number" && Number.isFinite(v)
        );
        if (!allNums) return null;

        const deltaLbX = [scoreX[0] - lbX[0], scoreX[1] - lbX[1]];
        const deltaUbX = [ubX[0] - scoreX[0], ubX[1] - scoreX[1]];

        const deltaLbY = [scoreY[0] - lbY[0], scoreY[1] - lbY[1]];
        const deltaUbY = [ubY[0] - scoreY[0], ubY[1] - scoreY[1]];

        const intervals = [
          [`${scoreX[0]} [${lbX[0]}, ${ubX[0]}]`, `${scoreY[0]} [${lbY[0]}, ${ubY[0]}]`],
          [`${scoreX[1]} [${lbX[1]}, ${ubX[1]}]`, `${scoreY[1]} [${lbY[1]}, ${ubY[1]}]`],
        ];

        const baseColor = colors[idx % colors.length];
        const faded = baseColor.replace("rgb", "rgba").replace(")", ",0.4)");

        return {
          x: scoreX,
          y: scoreY,

          error_x: {
            type: "data",
            array: deltaUbX,
            arrayminus: deltaLbX,
            visible: true,
            thickness: 2,
            width: 4,
            color: faded,
          },

          error_y: {
            type: "data",
            array: deltaUbY,
            arrayminus: deltaLbY,
            visible: true,
            thickness: 2,
            width: 4,
            color: faded,
          },

          mode: "lines+markers",
          type: "scatter",

          marker: {
            size: 12,
            opacity: [0.4, 1],
            color: baseColor,
          },

          line: { color: baseColor, width: 2 },

          name: proxyName,
          customdata: intervals,

          hovertemplate:
            `<b>%{data.name}</b>` +
            `<br>${activeDomains[0]?.label}: %{customdata[0]}` +
            `<br>${activeDomains[1]?.label}: %{customdata[1]}` +
            `<extra></extra>`,
        };
      })
      .filter(Boolean);

    return traces;
  }, [isReady, data, idxFrom, idxTo, activeDomains, activeProxies]);

  // --------------------
  // Render (centered container)
  // --------------------
  return (
    <div className="w-full flex flex-col items-center">
      {isLoading && <p className="text-center">Loading data...</p>}

      {!isLoading && chartLayout && chartData && (
        <div className="w-full flex justify-center">
          <div className="w-full max-w-[900px] aspect-square">
            <Plot
              data={chartData}
              layout={chartLayout}
              useResizeHandler={true}
              style={{ width: "100%", height: "100%" }}
              config={{ responsive: true, displayModeBar: false }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DumbbellPlot;